import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Care coordination subsection adapter
 * Handles care coordination fields (5 fields)
 */
export class CareCoordinationAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Direct field extractions for all care coordination fields
    const previousAppointments = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_PREVIOUS_APPOINTMENTS
    )?.at(0)

    const futureAppointments = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_FUTURE_APPOINTMENTS
    )?.at(0)

    const careTeamCoordination = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_CARE_TEAM_COORDINATION
    )?.at(0)

    const ordersForTreatment = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_ORDERS_FOR_TREATMENT
    )?.at(0)

    const dateOfVerbalCommunication = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION
    )?.at(0)

    return {
      [RocFormField.CARE_COORDINATION_PREVIOUS_APPOINTMENTS]: previousAppointments,
      [RocFormField.CARE_COORDINATION_FUTURE_APPOINTMENTS]: futureAppointments,
      [RocFormField.CARE_COORDINATION_CARE_TEAM_COORDINATION]: careTeamCoordination,
      [RocFormField.CARE_COORDINATION_ORDERS_FOR_TREATMENT]: ordersForTreatment,
      [RocFormField.CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION]: dateOfVerbalCommunication
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.CARE_COORDINATION_PREVIOUS_APPOINTMENTS)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_PREVIOUS_APPOINTMENTS,
        value: this.getFormDataField(RocFormField.CARE_COORDINATION_PREVIOUS_APPOINTMENTS)
      })
    }

    if (this.formDataFieldExists(RocFormField.CARE_COORDINATION_FUTURE_APPOINTMENTS)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_FUTURE_APPOINTMENTS,
        value: this.getFormDataField(RocFormField.CARE_COORDINATION_FUTURE_APPOINTMENTS)
      })
    }

    if (this.formDataFieldExists(RocFormField.CARE_COORDINATION_CARE_TEAM_COORDINATION)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_CARE_TEAM_COORDINATION,
        value: this.getFormDataField(RocFormField.CARE_COORDINATION_CARE_TEAM_COORDINATION)
      })
    }

    if (this.formDataFieldExists(RocFormField.CARE_COORDINATION_ORDERS_FOR_TREATMENT)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_ORDERS_FOR_TREATMENT,
        value: this.getFormDataField(RocFormField.CARE_COORDINATION_ORDERS_FOR_TREATMENT)
      })
    }

    if (this.formDataFieldExists(RocFormField.CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION,
        value: this.getFormDataField(RocFormField.CARE_COORDINATION_DATE_OF_VERBAL_COMMUNICATION)
      })
    }

    return mutations
  }
}
